/**
 * BILLING PAGE
 *
 * Full billing management page with 5 tabs:
 * 1. Plan — current plan + comparison grid
 * 2. Usage — credits, charts, transactions
 * 3. API Keys — BYOK key management
 * 4. Billing Info — company details + invoices
 * 5. Team Billing — seat management (Team plan only)
 */

import { useState, useEffect, useMemo } from "react";
import { useLocation } from "wouter";
import { useWorkspaceStore, type BillingMode } from "@/stores/workspaceStore";
import { useWorkspaceFeatures } from "@/hooks/useWorkspaceFeatures";
import {
  useBillingStore,
  type BillingPlan,
  type Subscription,
  type CreditBalance,
  type CreditTransaction,
  type Invoice,
} from "@/stores/billingStore";
import { useTeamStore } from "@/stores/teamStore";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  CreditCard,
  Key,
  Building,
  Users,
  Download,
  Check,
  X,
  AlertCircle,
  Coins,
  ArrowLeft,
  Loader2,
  Trash2,
  Plus,
  Minus,
  Eye,
  EyeOff,
  FileText,
} from "lucide-react";
import { cn, truncateId } from "@/lib/utils";
import { TopUpModal } from "@/features/billing/TopUpModal";
import { useToast } from "@/hooks/use-toast";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

const PLAN_COLORS: Record<string, string> = {
  free: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
  pro: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300",
  team: "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300",
  enterprise: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
};

const STATUS_COLORS: Record<string, string> = {
  active: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  canceling: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  canceled: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  past_due: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
};

const PIE_COLORS = ["#00B2C8", "#6366f1", "#f59e0b", "#ef4444", "#22c55e", "#8b5cf6"];

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export default function BillingPage() {
  const billingWsId = useWorkspaceStore((s) => s.getBillingWorkspaceId());
  const [, setLocation] = useLocation();

  if (billingWsId === null) {
    return (
      <div className="h-full flex flex-col" data-testid="container-billing-page">
        <div className="flex-1 flex items-center justify-center">
          <Card className="max-w-md">
            <CardContent className="pt-6 text-center space-y-4">
              <AlertCircle className="w-12 h-12 mx-auto text-muted-foreground" />
              <div>
                <h2 className="text-lg font-semibold">Sign in to access billing</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Sign in with your account to view plans, credits, and manage your subscription.
                </p>
              </div>
              <Button variant="outline" onClick={() => setLocation("/")}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return <BillingContent wsId={billingWsId} />;
}

function BillingContent({ wsId }: { wsId: number }) {
  const subscription = useBillingStore((s) => s.subscription);
  const credits = useBillingStore((s) => s.credits);
  const plans = useBillingStore((s) => s.plans);
  const fetchSubscription = useBillingStore((s) => s.fetchSubscription);
  const fetchCredits = useBillingStore((s) => s.fetchCredits);
  const fetchPlans = useBillingStore((s) => s.fetchPlans);
  const [activeTab, setActiveTab] = useState("plan");
  const [, setLocation] = useLocation();
  const { isLocalWorkspace } = useWorkspaceFeatures();
  const workspace = useWorkspaceStore((s) => s.getActiveWorkspace());
  const updateBillingMode = useWorkspaceStore((s) => s.updateBillingMode);

  useEffect(() => {
    fetchPlans();
    fetchSubscription(wsId);
    fetchCredits(wsId);
  }, [wsId]);

  const isTeamPlan = subscription?.plan?.name === "team";
  const isByok = subscription?.billingMode === "byok";
  const localBillingMode = workspace?.billingMode ?? "byok";

  const handleBillingModeChange = (mode: string) => {
    if (workspace?.id) {
      updateBillingMode(workspace.id, mode as BillingMode);
    }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden" data-testid="container-billing-page">
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-5xl mx-auto px-6 py-8">
          <div className="flex items-center gap-3 mb-6">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setLocation("/")} aria-label="Back to home">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Billing</h1>
              <p className="text-sm text-muted-foreground">Manage your plan, credits, and billing info</p>
            </div>
          </div>

          {/* Local workspace billing mode selector */}
          {isLocalWorkspace && (
            <Card className="mb-6">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">AI billing mode</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Choose how AI calls are billed for local task runs
                    </p>
                  </div>
                  <Select value={localBillingMode} onValueChange={handleBillingModeChange}>
                    <SelectTrigger className="w-[200px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="byok">
                        <div className="flex items-center gap-2">
                          <Key className="w-3.5 h-3.5" />
                          Own API key (BYOK)
                        </div>
                      </SelectItem>
                      <SelectItem value="credits">
                        <div className="flex items-center gap-2">
                          <Coins className="w-3.5 h-3.5" />
                          Platform credits
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {localBillingMode === "credits" && credits && (
                  <div className="mt-3 flex items-center gap-2 text-sm">
                    <Coins className="w-4 h-4 text-amber-500" />
                    <span>Balance: <strong>{formatCents(credits.balanceCents)}</strong></span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6">
              <TabsTrigger value="plan" className="gap-1.5">
                <CreditCard className="w-3.5 h-3.5" />
                Plan
              </TabsTrigger>
              <TabsTrigger value="usage" className="gap-1.5">
                <Coins className="w-3.5 h-3.5" />
                Usage
              </TabsTrigger>
              {isByok && (
                <TabsTrigger value="api-keys" className="gap-1.5">
                  <Key className="w-3.5 h-3.5" />
                  API Keys
                </TabsTrigger>
              )}
              <TabsTrigger value="billing-info" className="gap-1.5">
                <Building className="w-3.5 h-3.5" />
                Billing Info
              </TabsTrigger>
              {isTeamPlan && (
                <TabsTrigger value="team" className="gap-1.5">
                  <Users className="w-3.5 h-3.5" />
                  Team
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="plan">
              <PlanSection wsId={wsId} subscription={subscription} plans={plans} />
            </TabsContent>
            <TabsContent value="usage">
              <UsageSection wsId={wsId} credits={credits} />
            </TabsContent>
            {isByok && (
              <TabsContent value="api-keys">
                <ApiKeysSection wsId={wsId} />
              </TabsContent>
            )}
            <TabsContent value="billing-info">
              <BillingInfoSection wsId={wsId} />
            </TabsContent>
            {isTeamPlan && (
              <TabsContent value="team">
                <TeamBillingSection wsId={wsId} subscription={subscription} />
              </TabsContent>
            )}
          </Tabs>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 1: Plan
// ---------------------------------------------------------------------------

function PlanSection({
  wsId,
  subscription,
  plans,
}: {
  wsId: number;
  subscription: Subscription | null;
  plans: BillingPlan[];
}) {
  const subscribe = useBillingStore((s) => s.subscribe);
  const cancelSubscription = useBillingStore((s) => s.cancelSubscription);
  const isLoading = useBillingStore((s) => s.isLoading);
  const error = useBillingStore((s) => s.error);
  const currentPlan = subscription?.plan;

  const featureRows = [
    { label: "Projects", key: "maxProjects", format: (v: any) => v ?? "Unlimited" },
    { label: "Daily runs", key: "maxDailyRuns", format: (v: any) => v ?? "Unlimited" },
    { label: "BYOK allowed", key: "byokAllowed", format: (v: any) => (v ? "Yes" : "No") },
    { label: "Included credits", key: "includedCreditsCents", format: (v: any) => (v ? formatCents(v) : "—") },
    { label: "Credit markup", key: "creditMarkupPct", format: (v: any) => `${v}%` },
    { label: "Per seat pricing", key: "isPerSeat", format: (v: any) => (v ? "Yes" : "No") },
  ];

  return (
    <div className="space-y-6">
      {/* Current plan card */}
      {currentPlan && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CardTitle>Current Plan</CardTitle>
                <Badge className={cn("text-xs", PLAN_COLORS[currentPlan.name] || PLAN_COLORS.free)}>
                  {currentPlan.displayName}
                </Badge>
                <Badge className={cn("text-xs", STATUS_COLORS[subscription?.status || "active"])}>
                  {subscription?.status || "active"}
                </Badge>
              </div>
              {subscription?.billingMode && (
                <Badge variant="outline" className="text-xs">
                  {subscription.billingMode === "byok" ? "Bring Your Own Key" : "Credits"}
                </Badge>
              )}
            </div>
            {subscription?.currentPeriodEnd && (
              <CardDescription>
                Renews on {formatDate(subscription.currentPeriodEnd)}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Price:</span>{" "}
                <span className="font-medium">
                  {currentPlan.priceCents === 0
                    ? "Free"
                    : `${formatCents(currentPlan.priceCents)}/mo${currentPlan.isPerSeat ? " per seat" : ""}`}
                </span>
              </div>
              {subscription?.seatCount && subscription.seatCount > 1 && (
                <div>
                  <span className="text-muted-foreground">Seats:</span>{" "}
                  <span className="font-medium">{subscription.seatCount}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Plan comparison grid */}
      {plans.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Available Plans</CardTitle>
            <CardDescription>Compare plans and choose the best fit</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-40">Feature</TableHead>
                    {plans.map((plan) => (
                      <TableHead key={plan.id} className="text-center min-w-[140px]">
                        <div className="space-y-1">
                          <Badge className={cn("text-xs", PLAN_COLORS[plan.name] || PLAN_COLORS.free)}>
                            {plan.displayName}
                          </Badge>
                          <div className="font-semibold">
                            {plan.priceCents === 0
                              ? "Free"
                              : `${formatCents(plan.priceCents)}/mo`}
                          </div>
                        </div>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {featureRows.map((row) => (
                    <TableRow key={row.key}>
                      <TableCell className="font-medium text-muted-foreground">{row.label}</TableCell>
                      {plans.map((plan) => (
                        <TableCell key={plan.id} className="text-center">
                          {row.format((plan as any)[row.key])}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell />
                    {plans.map((plan) => (
                      <TableCell key={plan.id} className="text-center">
                        {currentPlan?.name === plan.name ? (
                          <Badge variant="outline" className="text-xs">Current</Badge>
                        ) : (
                          <Button
                            size="sm"
                            variant={plan.priceCents > (currentPlan?.priceCents || 0) ? "default" : "outline"}
                            disabled={isLoading || !plan.stripePriceId && plan.priceCents > 0}
                            onClick={() => subscribe(wsId, plan.name, "credits")}
                          >
                            {plan.priceCents > (currentPlan?.priceCents || 0) ? "Upgrade" : "Switch"}
                          </Button>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cancel subscription */}
      {subscription?.status === "active" && currentPlan && currentPlan.name !== "free" && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Cancel subscription</p>
                <p className="text-xs text-muted-foreground">
                  You'll keep access until the end of your billing period
                </p>
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="sm" disabled={isLoading}>
                    Cancel Plan
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Cancel subscription?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Your {currentPlan.displayName} plan will be downgraded to Free at the end of
                      the current billing period. This cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Keep Plan</AlertDialogCancel>
                    <AlertDialogAction
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      onClick={() => cancelSubscription(wsId)}
                    >
                      Cancel Plan
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </CardContent>
        </Card>
      )}

      {error && (
        <p className="text-sm text-destructive flex items-center gap-1.5">
          <AlertCircle className="w-3.5 h-3.5" />
          {error === "admin_required" ? "You need admin access to change the plan." : error}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 2: Usage
// ---------------------------------------------------------------------------

function UsageSection({
  wsId,
  credits,
}: {
  wsId: number;
  credits: CreditBalance | null;
}) {
  const usage = useBillingStore((s) => s.usage);
  const transactions = useBillingStore((s) => s.transactions);
  const fetchUsage = useBillingStore((s) => s.fetchUsage);
  const fetchTransactions = useBillingStore((s) => s.fetchTransactions);
  const [txnOffset, setTxnOffset] = useState(0);
  const [hasMoreTxns, setHasMoreTxns] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [topUpOpen, setTopUpOpen] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (!loaded) {
      fetchUsage(wsId);
      fetchTransactions(wsId, 20, 0).then((txns) => {
        if (txns.length < 20) setHasMoreTxns(false);
      }).catch(() => {
        toast({ title: "Failed to load transactions", variant: "destructive" });
      });
      setLoaded(true);
    }
  }, [wsId, loaded]);

  const loadMoreTxns = async () => {
    const newOffset = txnOffset + 20;
    const txns = await fetchTransactions(wsId, 20, newOffset);
    setTxnOffset(newOffset);
    if (txns.length < 20) setHasMoreTxns(false);
  };

  // Transform by_day map → sorted array with zero-fill
  const dailyData = useMemo(() => {
    if (!usage?.byDay) return [];
    const entries = Object.entries(usage.byDay).map(([date, val]) => ({
      date,
      cost: (val.costCents || 0) / 100,
      events: val.events || 0,
    }));
    entries.sort((a, b) => a.date.localeCompare(b.date));
    if (entries.length < 2) return entries;

    // Zero-fill missing dates
    const filled: typeof entries = [];
    const start = new Date(entries[0].date);
    const end = new Date(entries[entries.length - 1].date);
    const lookup = new Map(entries.map((e) => [e.date, e]));
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const key = d.toISOString().split("T")[0];
      filled.push(lookup.get(key) || { date: key, cost: 0, events: 0 });
    }
    return filled;
  }, [usage?.byDay]);

  // Transform by_model map → array for pie chart
  const modelData = useMemo(() => {
    if (!usage?.byModel) return [];
    return Object.entries(usage.byModel).map(([name, val], i) => ({
      name,
      value: (val.costCents || 0) / 100,
      fill: PIE_COLORS[i % PIE_COLORS.length],
    }));
  }, [usage?.byModel]);

  // Transform by_project map → array for table
  const projectData = useMemo(() => {
    if (!usage?.byProject) return [];
    return Object.entries(usage.byProject)
      .map(([id, val]) => ({
        projectId: id,
        events: val.events || 0,
        cost: (val.costCents || 0) / 100,
      }))
      .sort((a, b) => b.cost - a.cost);
  }, [usage?.byProject]);

  const balancePct = credits
    ? credits.lifetimePurchasedCents > 0
      ? Math.min(100, ((credits.lifetimePurchasedCents - credits.balanceCents) / credits.lifetimePurchasedCents) * 100)
      : 0
    : 0;

  return (
    <div className="space-y-6">
      {/* Credit balance */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Credit Balance</CardTitle>
            <Button size="sm" className="gap-1.5" onClick={() => setTopUpOpen(true)}>
              <Plus className="w-3.5 h-3.5" />
              Top up
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-3xl font-bold tabular-nums">
            {credits ? formatCents(credits.balanceCents) : "$0.00"}
          </div>
          {credits && credits.lifetimePurchasedCents > 0 && (
            <div className="space-y-2">
              <Progress value={balancePct} className="h-2" />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Used: {formatCents(credits.lifetimeUsedCents)}</span>
                <span>Purchased: {formatCents(credits.lifetimePurchasedCents)}</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Usage chart — daily cost */}
      {dailyData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Daily Cost (30 days)</CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer
              config={{
                cost: { label: "Cost ($)", color: "#00B2C8" },
              }}
              className="h-[250px] w-full"
            >
              <AreaChart data={dailyData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border/30" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(v: string) => v.slice(5)}
                  className="text-xs"
                />
                <YAxis tickFormatter={(v: number) => `$${v}`} className="text-xs" />
                <ChartTooltip
                  content={
                    <ChartTooltipContent
                      formatter={(value) => `$${Number(value).toFixed(2)}`}
                    />
                  }
                />
                <Area
                  type="monotone"
                  dataKey="cost"
                  stroke="var(--color-cost)"
                  fill="var(--color-cost)"
                  fillOpacity={0.15}
                  strokeWidth={2}
                />
              </AreaChart>
            </ChartContainer>
          </CardContent>
        </Card>
      )}

      {/* Model breakdown + Project breakdown side by side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {modelData.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Cost by Model</CardTitle>
            </CardHeader>
            <CardContent>
              <ChartContainer
                config={Object.fromEntries(
                  modelData.map((m) => [m.name, { label: m.name, color: m.fill }]),
                )}
                className="h-[200px] w-full"
              >
                <PieChart>
                  <ChartTooltip
                    content={
                      <ChartTooltipContent
                        formatter={(value) => `$${Number(value).toFixed(2)}`}
                      />
                    }
                  />
                  <Pie
                    data={modelData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    paddingAngle={2}
                  >
                    {modelData.map((entry, i) => (
                      <Cell key={i} fill={entry.fill} />
                    ))}
                  </Pie>
                </PieChart>
              </ChartContainer>
              <div className="mt-3 space-y-1">
                {modelData.map((m) => (
                  <div key={m.name} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: m.fill }} />
                      <span className="text-muted-foreground">{m.name}</span>
                    </div>
                    <span className="font-medium tabular-nums">${m.value.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {projectData.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Cost by Project</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Project</TableHead>
                    <TableHead className="text-right">Events</TableHead>
                    <TableHead className="text-right">Cost</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {projectData.map((p) => (
                    <TableRow key={p.projectId}>
                      <TableCell className="font-medium">#{p.projectId}</TableCell>
                      <TableCell className="text-right tabular-nums">{p.events}</TableCell>
                      <TableCell className="text-right tabular-nums">${p.cost.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Transactions */}
      <Card>
        <CardHeader>
          <CardTitle>Transaction History</CardTitle>
        </CardHeader>
        <CardContent>
          {transactions.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No transactions yet</p>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.map((txn) => (
                    <TransactionRow key={txn.id} txn={txn} />
                  ))}
                </TableBody>
              </Table>
              {hasMoreTxns && (
                <div className="text-center mt-4">
                  <Button variant="outline" size="sm" onClick={loadMoreTxns}>
                    Load more
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <TopUpModal open={topUpOpen} onOpenChange={setTopUpOpen} wsId={wsId} />
    </div>
  );
}

function TransactionRow({ txn }: { txn: CreditTransaction }) {
  const isPositive = txn.amountCents > 0;
  const typeColors: Record<string, string> = {
    purchase: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
    included: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
    usage: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
    refund: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  };

  return (
    <TableRow>
      <TableCell className="text-xs text-muted-foreground">{formatDate(txn.createdAt)}</TableCell>
      <TableCell>
        <Badge className={cn("text-[10px]", typeColors[txn.type] || typeColors.usage)}>
          {txn.type}
        </Badge>
      </TableCell>
      <TableCell className="text-sm">{txn.description || "—"}</TableCell>
      <TableCell className={cn("text-right tabular-nums font-medium text-sm", isPositive ? "text-emerald-600" : "text-red-500")}>
        {isPositive ? "+" : ""}
        {formatCents(txn.amountCents)}
      </TableCell>
      <TableCell className="text-right tabular-nums text-sm">{formatCents(txn.balanceAfterCents)}</TableCell>
    </TableRow>
  );
}

// ---------------------------------------------------------------------------
// Tab 3: API Keys
// ---------------------------------------------------------------------------

function ApiKeysSection({ wsId }: { wsId: number }) {
  const apiKeys = useBillingStore((s) => s.apiKeys);
  const fetchApiKeys = useBillingStore((s) => s.fetchApiKeys);
  const addApiKey = useBillingStore((s) => s.addApiKey);
  const removeApiKey = useBillingStore((s) => s.removeApiKey);
  const error = useBillingStore((s) => s.error);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!loaded) {
      fetchApiKeys(wsId);
      setLoaded(true);
    }
  }, [wsId, loaded]);

  if (error === "admin_required") {
    return (
      <Card>
        <CardContent className="pt-6 text-center space-y-2">
          <AlertCircle className="w-8 h-8 mx-auto text-muted-foreground" />
          <p className="text-sm font-medium">Admin access required</p>
          <p className="text-xs text-muted-foreground">
            You need admin or owner access to manage API keys.
          </p>
        </CardContent>
      </Card>
    );
  }

  const providers = [
    { id: "anthropic", name: "Anthropic", placeholder: "sk-ant-..." },
    { id: "openai", name: "OpenAI", placeholder: "sk-..." },
  ];

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>API Keys (BYOK)</CardTitle>
          <CardDescription>
            Bring your own API keys. Keys are encrypted and validated on save.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {providers.map((provider) => (
            <ApiKeyRow
              key={provider.id}
              wsId={wsId}
              provider={provider}
              existing={apiKeys.find((k) => k.provider === provider.id)}
              onAdd={addApiKey}
              onRemove={removeApiKey}
            />
          ))}
        </CardContent>
      </Card>
      {error && error !== "admin_required" && (
        <p className="text-sm text-destructive flex items-center gap-1.5">
          <AlertCircle className="w-3.5 h-3.5" />
          {error}
        </p>
      )}
    </div>
  );
}

function ApiKeyRow({
  wsId,
  provider,
  existing,
  onAdd,
  onRemove,
}: {
  wsId: number;
  provider: { id: string; name: string; placeholder: string };
  existing?: { keyHint: string | null; isValid: boolean };
  onAdd: (wsId: number, provider: string, key: string) => Promise<boolean>;
  onRemove: (wsId: number, provider: string) => Promise<boolean>;
}) {
  const [newKey, setNewKey] = useState("");
  const [showInput, setShowInput] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showKey, setShowKey] = useState(false);

  const handleSave = async () => {
    if (!newKey.trim()) return;
    setSaving(true);
    const ok = await onAdd(wsId, provider.id, newKey.trim());
    setSaving(false);
    if (ok) {
      setNewKey("");
      setShowInput(false);
    }
  };

  return (
    <div className="flex items-start gap-4 p-4 rounded-lg border bg-muted/20">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-medium text-sm">{provider.name}</span>
          {existing ? (
            <div className={cn("w-2 h-2 rounded-full", existing.isValid ? "bg-emerald-500" : "bg-red-500")} />
          ) : (
            <div className="w-2 h-2 rounded-full bg-gray-400" />
          )}
        </div>

        {existing && !showInput ? (
          <div className="flex items-center gap-2">
            <code className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
              {existing.keyHint || "••••••••"}
            </code>
            <Badge variant="outline" className="text-[10px]">
              {existing.isValid ? "Valid" : "Invalid"}
            </Badge>
          </div>
        ) : !showInput ? (
          <p className="text-xs text-muted-foreground">No key configured</p>
        ) : null}

        {showInput && (
          <div className="flex gap-2 mt-2">
            <div className="relative flex-1">
              <Input
                type={showKey ? "text" : "password"}
                placeholder={provider.placeholder}
                value={newKey}
                onChange={(e) => setNewKey(e.target.value)}
                className="pr-8 h-8 text-xs font-mono"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                onClick={() => setShowKey(!showKey)}
              >
                {showKey ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              </button>
            </div>
            <Button size="sm" className="h-8" onClick={handleSave} disabled={saving || !newKey.trim()}>
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save"}
            </Button>
            <Button size="sm" variant="ghost" className="h-8" onClick={() => { setShowInput(false); setNewKey(""); }}>
              Cancel
            </Button>
          </div>
        )}
      </div>

      <div className="flex gap-1.5">
        <Button
          size="sm"
          variant="outline"
          className="h-8"
          onClick={() => setShowInput(true)}
        >
          {existing ? "Replace" : "Add"}
        </Button>
        {existing && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" variant="ghost" className="h-8 text-destructive hover:text-destructive" aria-label={`Delete ${provider.name} key`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete {provider.name} key?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently remove the stored API key. Any workflows using this key will fail.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={() => onRemove(wsId, provider.id)}
                >
                  Delete Key
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 4: Billing Info
// ---------------------------------------------------------------------------

function BillingInfoSection({ wsId }: { wsId: number }) {
  const billingDetails = useBillingStore((s) => s.billingDetails);
  const invoices = useBillingStore((s) => s.invoices);
  const fetchBillingDetails = useBillingStore((s) => s.fetchBillingDetails);
  const updateBillingDetails = useBillingStore((s) => s.updateBillingDetails);
  const fetchInvoices = useBillingStore((s) => s.fetchInvoices);
  const error = useBillingStore((s) => s.error);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [invOffset, setInvOffset] = useState(0);
  const [hasMoreInv, setHasMoreInv] = useState(true);
  const { toast } = useToast();

  // Form state
  const [form, setForm] = useState({
    companyName: "",
    billingEmail: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    postalCode: "",
    country: "NL",
    vatNumber: "",
  });

  useEffect(() => {
    if (!loaded) {
      fetchBillingDetails(wsId);
      fetchInvoices(wsId, 20, 0).then((invs) => {
        if (invs.length < 20) setHasMoreInv(false);
      }).catch(() => {
        toast({ title: "Failed to load invoices", variant: "destructive" });
      });
      setLoaded(true);
    }
  }, [wsId, loaded]);

  // Populate form when billing details load
  useEffect(() => {
    if (billingDetails) {
      setForm({
        companyName: billingDetails.companyName || "",
        billingEmail: billingDetails.billingEmail || "",
        addressLine1: billingDetails.addressLine1 || "",
        addressLine2: billingDetails.addressLine2 || "",
        city: billingDetails.city || "",
        state: billingDetails.state || "",
        postalCode: billingDetails.postalCode || "",
        country: billingDetails.country || "NL",
        vatNumber: billingDetails.vatNumber || "",
      });
    }
  }, [billingDetails]);

  const handleSave = async () => {
    setSaving(true);
    await updateBillingDetails(wsId, form);
    setSaving(false);
  };

  const loadMoreInvoices = async () => {
    const newOffset = invOffset + 20;
    const invs = await fetchInvoices(wsId, 20, newOffset);
    setInvOffset(newOffset);
    if (invs.length < 20) setHasMoreInv(false);
  };

  const isAdminError = error === "admin_required";

  const updateField = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-6">
      {/* Billing details form */}
      <Card>
        <CardHeader>
          <CardTitle>Billing Details</CardTitle>
          <CardDescription>Company information for invoices</CardDescription>
        </CardHeader>
        <CardContent>
          {isAdminError ? (
            <div className="text-center py-4 space-y-2">
              <AlertCircle className="w-8 h-8 mx-auto text-muted-foreground" />
              <p className="text-sm">Admin access required to update billing details.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="companyName">Company name</Label>
                <Input
                  id="companyName"
                  value={form.companyName}
                  onChange={(e) => updateField("companyName", e.target.value)}
                  placeholder="Acme Inc."
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="billingEmail">Billing email</Label>
                <Input
                  id="billingEmail"
                  type="email"
                  value={form.billingEmail}
                  onChange={(e) => updateField("billingEmail", e.target.value)}
                  placeholder="billing@company.com"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="addressLine1">Address line 1</Label>
                <Input
                  id="addressLine1"
                  value={form.addressLine1}
                  onChange={(e) => updateField("addressLine1", e.target.value)}
                  placeholder="123 Main St"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="addressLine2">Address line 2</Label>
                <Input
                  id="addressLine2"
                  value={form.addressLine2}
                  onChange={(e) => updateField("addressLine2", e.target.value)}
                  placeholder="Suite 100"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={form.city}
                  onChange={(e) => updateField("city", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state">State / Province</Label>
                <Input
                  id="state"
                  value={form.state}
                  onChange={(e) => updateField("state", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="postalCode">Postal code</Label>
                <Input
                  id="postalCode"
                  value={form.postalCode}
                  onChange={(e) => updateField("postalCode", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="country">Country</Label>
                <Select value={form.country} onValueChange={(v) => updateField("country", v)}>
                  <SelectTrigger id="country">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NL">Netherlands</SelectItem>
                    <SelectItem value="US">United States</SelectItem>
                    <SelectItem value="GB">United Kingdom</SelectItem>
                    <SelectItem value="DE">Germany</SelectItem>
                    <SelectItem value="FR">France</SelectItem>
                    <SelectItem value="BE">Belgium</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="vatNumber">VAT number</Label>
                <Input
                  id="vatNumber"
                  value={form.vatNumber}
                  onChange={(e) => updateField("vatNumber", e.target.value)}
                  placeholder="NL123456789B01"
                />
              </div>
              <div className="md:col-span-2 flex justify-end">
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Save Details
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invoices */}
      <Card>
        <CardHeader>
          <CardTitle>Invoices</CardTitle>
        </CardHeader>
        <CardContent>
          {invoices.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No invoices yet</p>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">PDF</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((inv) => (
                    <InvoiceRow key={inv.id} inv={inv} wsId={wsId} />
                  ))}
                </TableBody>
              </Table>
              {hasMoreInv && (
                <div className="text-center mt-4">
                  <Button variant="outline" size="sm" onClick={loadMoreInvoices}>
                    Load more
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {error && !isAdminError && (
        <p className="text-sm text-destructive flex items-center gap-1.5">
          <AlertCircle className="w-3.5 h-3.5" />
          {error}
        </p>
      )}
    </div>
  );
}

function InvoiceRow({ inv, wsId }: { inv: Invoice; wsId: number }) {
  const statusColor: Record<string, string> = {
    paid: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
    draft: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
    sent: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
    void: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  };

  return (
    <TableRow>
      <TableCell className="font-medium text-sm">{inv.invoiceNumber}</TableCell>
      <TableCell className="text-xs text-muted-foreground">
        {formatDate(inv.periodStart)} — {formatDate(inv.periodEnd)}
      </TableCell>
      <TableCell className="tabular-nums font-medium text-sm">
        {formatCents(inv.totalCents)} {inv.currency.toUpperCase()}
      </TableCell>
      <TableCell>
        <Badge className={cn("text-[10px]", statusColor[inv.status] || statusColor.draft)}>
          {inv.status}
        </Badge>
      </TableCell>
      <TableCell className="text-right">
        {inv.pdfUrl ? (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 gap-1"
            onClick={() => window.open(inv.pdfUrl!, "_blank")}
          >
            <Download className="w-3.5 h-3.5" />
          </Button>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 gap-1"
            onClick={() => {
              // Use the PDF endpoint which generates on-the-fly if needed
              window.open(`/api/workspaces/${wsId}/billing/invoices/${inv.id}/pdf`, "_blank");
            }}
          >
            <FileText className="w-3.5 h-3.5" />
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}

// ---------------------------------------------------------------------------
// Tab 5: Team Billing
// ---------------------------------------------------------------------------

function TeamBillingSection({
  wsId,
  subscription,
}: {
  wsId: number;
  subscription: Subscription | null;
}) {
  const memberUsage = useBillingStore((s) => s.memberUsage);
  const usage = useBillingStore((s) => s.usage);
  const fetchMemberUsage = useBillingStore((s) => s.fetchMemberUsage);
  const fetchUsage = useBillingStore((s) => s.fetchUsage);
  const updateSeats = useBillingStore((s) => s.updateSeats);
  const isLoading = useBillingStore((s) => s.isLoading);
  const error = useBillingStore((s) => s.error);
  const members = useTeamStore((s) => s.members);
  const fetchMembers = useTeamStore((s) => s.fetchMembers);
  const [showReduceWarning, setShowReduceWarning] = useState(false);

  useEffect(() => {
    fetchMemberUsage(wsId);
    fetchMembers(wsId);
    fetchUsage(wsId);
  }, [wsId]);

  const seatCount = subscription?.seatCount || 1;
  const plan = subscription?.plan;
  const pricePerSeat = plan?.priceCents || 0;
  const totalMonthly = seatCount * pricePerSeat;

  const activeMemberCount = members.filter((m) => m.invitation_status === "accepted").length;

  const handleAddSeat = () => {
    updateSeats(wsId, seatCount + 1);
  };

  const handleRemoveSeat = () => {
    const newCount = seatCount - 1;
    if (newCount < activeMemberCount) {
      setShowReduceWarning(true);
    } else {
      updateSeats(wsId, newCount);
    }
  };

  const confirmReduce = () => {
    setShowReduceWarning(false);
    updateSeats(wsId, seatCount - 1);
  };

  const usageTotal = memberUsage.reduce((sum, m) => sum + m.totalBilledCents, 0);

  const chartData = memberUsage.map((m, i) => ({
    name: m.name || m.email || truncateId(m.userId),
    cost: m.totalBilledCents / 100,
    fill: PIE_COLORS[i % PIE_COLORS.length],
  }));

  return (
    <div className="space-y-6">
      {/* Seat management */}
      <Card>
        <CardHeader>
          <CardTitle>Team Seats</CardTitle>
          <CardDescription>Manage your team's seat allocation</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-6">
            <div>
              <div className="text-3xl font-bold tabular-nums">{seatCount}</div>
              <div className="text-xs text-muted-foreground">Active seats</div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="icon"
                variant="outline"
                className="h-8 w-8"
                disabled={seatCount <= 1 || isLoading}
                onClick={handleRemoveSeat}
                aria-label="Remove seat"
              >
                <Minus className="w-4 h-4" />
              </Button>
              <Button
                size="icon"
                variant="outline"
                className="h-8 w-8"
                disabled={isLoading}
                onClick={handleAddSeat}
                aria-label="Add seat"
              >
                <Plus className="w-4 h-4" />
              </Button>
              {isLoading && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
            </div>
          </div>
          {pricePerSeat > 0 && (
            <p className="text-sm text-muted-foreground">
              {seatCount} seat{seatCount !== 1 ? "s" : ""} &times; {formatCents(pricePerSeat)}/seat ={" "}
              <span className="font-medium text-foreground">{formatCents(totalMonthly)}/mo</span>
            </p>
          )}
        </CardContent>
      </Card>

      {/* Volume discount indicator */}
      {plan && (
        <Card>
          <CardHeader>
            <CardTitle>Plan Discount</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Credit markup</span>
              <Badge variant="outline">{plan.creditMarkupPct}%</Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              Team discount active: {plan.creditMarkupPct}% markup (vs 50% on Pro)
            </p>
            {(() => {
              // Enterprise tier threshold: $5,000/mo spend unlocks 30% markup
              const enterpriseThreshold = 500000; // cents
              const currentSpend = usage?.totalBilledCostCents ?? 0;
              const progressPct = Math.min(100, (currentSpend / enterpriseThreshold) * 100);
              return (
                <div className="space-y-1">
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Enterprise tier (30% markup)</span>
                    <span>{formatCents(currentSpend)} / {formatCents(enterpriseThreshold)}</span>
                  </div>
                  {usage ? (
                    <Progress value={progressPct} className="h-2" />
                  ) : (
                    <div className="h-2 w-full bg-muted rounded animate-pulse" />
                  )}
                  <p className="text-[11px] text-muted-foreground">
                    {usage
                      ? currentSpend >= enterpriseThreshold
                        ? "You qualify for Enterprise pricing — contact sales"
                        : `${formatCents(enterpriseThreshold - currentSpend)} more spend to unlock Enterprise tier`
                      : "Loading usage..."}
                  </p>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Per-member usage chart */}
      {chartData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Usage by Member</CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer
              config={Object.fromEntries(
                chartData.map((d) => [d.name, { label: d.name, color: d.fill }]),
              )}
              className="h-[250px] w-full"
            >
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border/30" />
                <XAxis
                  dataKey="name"
                  className="text-xs"
                  tickFormatter={(v: string) => (v.length > 12 ? v.slice(0, 12) + "..." : v)}
                />
                <YAxis tickFormatter={(v: number) => `$${v}`} className="text-xs" />
                <ChartTooltip
                  content={
                    <ChartTooltipContent
                      formatter={(value) => `$${Number(value).toFixed(2)}`}
                    />
                  }
                />
                <Bar dataKey="cost" radius={[4, 4, 0, 0]}>
                  {chartData.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ChartContainer>
          </CardContent>
        </Card>
      )}

      {/* Per-member usage table */}
      <Card>
        <CardHeader>
          <CardTitle>Member Usage Details</CardTitle>
        </CardHeader>
        <CardContent>
          {memberUsage.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              No usage data for this period
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Member</TableHead>
                  <TableHead className="text-right">Runs</TableHead>
                  <TableHead className="text-right">Cost</TableHead>
                  <TableHead className="text-right">% of total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {memberUsage.map((m) => (
                  <TableRow key={m.userId}>
                    <TableCell>
                      <div>
                        <span className="font-medium text-sm">{m.name || "Unknown"}</span>
                        {m.email && (
                          <span className="text-xs text-muted-foreground ml-2">{m.email}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{m.totalEvents}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium">
                      {formatCents(m.totalBilledCents)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">
                      {usageTotal > 0
                        ? `${((m.totalBilledCents / usageTotal) * 100).toFixed(0)}%`
                        : "0%"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Reduce seats warning dialog */}
      <AlertDialog open={showReduceWarning} onOpenChange={setShowReduceWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reduce seats?</AlertDialogTitle>
            <AlertDialogDescription>
              You have {activeMemberCount} active member{activeMemberCount !== 1 ? "s" : ""}.
              Reducing to {seatCount - 1} seat{seatCount - 1 !== 1 ? "s" : ""} may prevent some
              members from using the workspace.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmReduce}>Reduce Seats</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {error && (
        <p className="text-sm text-destructive flex items-center gap-1.5">
          <AlertCircle className="w-3.5 h-3.5" />
          {error === "admin_required" ? "You need admin access to manage seats." : error}
        </p>
      )}
    </div>
  );
}
